import { Box, ColumnLayout, Container, Header, Link, SpaceBetween } from '@cloudscape-design/components';
import { DashboardViewLayout } from '../../../../components/DashboardViewLayout';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getDripContact } from '../../../../common/api/dripContacts';
import { DripContact } from '../../../../common/types';
import { LoadingOrError } from '../../../../components/Loading';
import { DripEmailTable } from '../../../../components/dashboard/DripEmailTable';
import { Email } from '../../../../components/Email';
import { useMemo, useState } from 'react';
import { formatName } from '../../../../common/names';

// We're already in the scorecard app
const scorecardUrl = '';

export default function View() {
  const navigate = useNavigate();
  const resourceName = 'Drip Contacts';
  const { uuid } = useParams({ from: '/dashboard/drip/contacts_/$uuid' });

  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: ['drip_contacts', uuid],
    queryFn: async () => (uuid ? await getDripContact(uuid) : null),
    staleTime: 600_000,
  });

  const loading = (isPending || isFetching) && !error;

  const publicUnsubscribeUrl = useMemo(() => {
    if (!data?.uuid) {
      return '';
    }
    return `${scorecardUrl}/drip/contacts/${data.uuid}/opt-out`;
  }, [data?.uuid]);

  return (
    <DashboardViewLayout
      headerVariant="high-contrast"
      breadcrumbs={[
        { text: resourceName, href: '/dashboard/drip/contacts' },
        { text: data ? `${data.first_name} ${data.last_name}` : '', href: '' },
      ]}
      header={
        data && (
          <Container
            header={
              <Header
                variant="h1"
                description={
                  <SpaceBetween direction="vertical" size="xxxs">
                    <Link
                      href={`/dashboard/places/${data.place?.uuid}`}
                      variant="secondary"
                      onFollow={(e) => {
                        e.preventDefault();
                        navigate({ to: `/dashboard/places/${data.place?.uuid}` });
                      }}
                    >
                      {data.place?.name}
                    </Link>
                    <Box variant="p">{data.emails.length} emails</Box>
                  </SpaceBetween>
                }
              >
                {formatName(data.first_name, data.last_name)}
                {data.job_title ? ` - ${data.job_title}` : ''}
              </Header>
            }
          >
            <SpaceBetween size="m">
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Email</Box>
                  <Email email={data.email} />
                </div>
                {data.crm_id && (
                  <div>
                    <Box variant="awsui-key-label">CRM ID</Box>
                    <Box variant="p">{data.crm_id}</Box>
                  </div>
                )}
                <div>
                  <Box variant="awsui-key-label">Opt Out</Box>
                  <Box variant="p">{data.opt_out ? 'Yes' : 'No'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Public unsubscribe link</Box>
                  {publicUnsubscribeUrl ? (
                    <Link href={publicUnsubscribeUrl} external>
                      {publicUnsubscribeUrl}
                    </Link>
                  ) : (
                    <Box variant="p">Unavailable</Box>
                  )}
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        )
      }
    >
      {loading || error ? (
        <LoadingOrError loading={loading} error={error} retry={refetch} />
      ) : data ? (
        <ContactView contact={data} />
      ) : null}
    </DashboardViewLayout>
  );
}

export function ContactView({ contact }: { contact: DripContact }) {
  const [now, setNow] = useState(new Date());
  return (
    <Container>
      <DripEmailTable
        variant="embedded"
        now={now}
        setNow={setNow}
        query={{
          tokens: [{ propertyKey: 'contact_email', value: contact.email, operator: '=' }],
          operation: 'and',
        }}
        hideFilter
      />
    </Container>
  );
}
